import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'visitor_city_v1';

export interface VisitorCity {
  city: string;          // display name, e.g. "Cluj-Napoca"
  city_norm: string;     // normalized lowercase no-diacritics
  lat: number;
  lng: number;
}

function read(): VisitorCity | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.lat === 'number' && typeof parsed.lng === 'number') return parsed;
    return null;
  } catch {
    return null;
  }
}

export function useVisitorCity() {
  const [city, setCityState] = useState<VisitorCity | null>(() => read());

  useEffect(() => {
    const handler = () => setCityState(read());
    window.addEventListener('visitor-city-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('visitor-city-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setCity = useCallback((c: VisitorCity | null) => {
    if (c) localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    else localStorage.removeItem(STORAGE_KEY);
    setCityState(c);
    window.dispatchEvent(new Event('visitor-city-changed'));
  }, []);

  return { city, setCity };
}

// Haversine distance in km
export function distanceKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number }
): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
